import { useEffect, useMemo, useState } from 'react';
import { api, type BillGroupRow } from '../lib/api';
import { money } from './DataTable';
import Modal from './Modal';
import { canScreen, useMe } from '../lib/me';

/**
 * 통합 청구 묶음(2026-11-02 미팅 #48 way) — 미수금 › 미청구 위칸 버튼 → 묶음 창.
 *
 * way: 「법인이 넷이라 나눠 청구하는데 기사들은 노선을 섞어 탄다 → 통합 청구로 묶어, 청구가 그 접수에 없어도 지급」 ·
 *      「통합 청구를 누르면 그룹으로 묶인 상태로 보이면서 지급 노선만 하나로 관리 — 뷰로」.
 * - 묶음 안 한 접수라도 그 달 청구(청구대상~)되면 나머지 접수도 기사 명세에 운행료·영업정산이 들어간다(서버 `salaryGen.ts GROUP_OPEN`).
 * - 묶음별로 그 달 청구·운행료·수익 합계(묶음 = 거래처 하나처럼) · 접수마다 상태.
 * - 넣기·빼기 = 관리자만(11-02 way · 서버 `saveRecvBillGroup` · 시작월 = 기사 마감 안 된 달). 보기 = 회계 조회 — 묶을 접수는 관리자에게 요청.
 */
const STATE_TONE: Record<string, string> = {
  '청구 있음': 'text-emerald-700', '묶음 청구로 열림': 'text-sky-700', '닫힘': 'text-red-600', '시작 전': 'text-ink-faint',
};

export default function BillGroupPanel({ mon, onChanged }: { mon: string; onChanged?: () => void }) {
  const { me } = useMe();
  const canView = canScreen(me, 'accounting', 'read');
  const canWrite = me?.role === 'admin';
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<BillGroupRow[] | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState('');
  const [add, setAdd] = useState<Record<number, { recvId: string; fromMon: string }>>({});

  const load = async () => {
    if (!mon) return;
    try { setRows((await api.listRecvBillGroups({ mon })).rows); setErr(''); } catch (e) { setErr((e as Error).message); }
  };
  useEffect(() => { if (canView) void load(); }, [mon, canView]); // eslint-disable-line react-hooks/exhaustive-deps

  const groups = useMemo(() => {
    const m = new Map<number, { id: number; name: string; note: string | null; members: BillGroupRow[] }>();
    for (const r of rows ?? []) {
      const g = m.get(r.group_id) ?? { id: r.group_id, name: r.group_name, note: r.note, members: [] };
      if (r.member_id !== null) g.members.push(r);
      m.set(r.group_id, g);
    }
    return [...m.values()];
  }, [rows]);

  const run = async (f: () => Promise<unknown>) => {
    setBusy(true);
    try { await f(); await load(); onChanged?.(); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  const sum = (ms: BillGroupRow[], k: 'cost_cust_total' | 'cost_driver_total' | 'profit') => ms.reduce((a, r) => a + Number(r[k] || 0), 0);

  if (!canView) return null;
  return (
    <>
      <button
        type="button"
        className="btn-ghost whitespace-nowrap text-xs"
        title="여러 접수를 하나의 청구로 묶음 — 묶음 안 한 접수만 청구돼도 나머지 접수의 기사 지급이 열립니다"
        onClick={() => setOpen(true)}
      >
        통합 청구{groups.length ? ` (${groups.length})` : ''}
      </button>
      {open && (
        <Modal width="max-w-5xl">
          <div className="card space-y-3 p-5">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold">통합 청구 묶음 · {mon}</h2>
              <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>닫기</button>
            </div>
            <p className="text-xs text-ink-soft">
              청구는 접수(법인)별로 하고, 지급은 묶음으로 봅니다. 묶음 안 <b>한 접수라도 이 달 청구 요청(청구대상)</b>이 되면
              청구가 없는 나머지 접수도 기사 명세에 운행료·영업정산이 들어갑니다. 묶음 전체가 청구 전이면 아무도 열리지 않습니다.
              {!canWrite && <> 묶음 넣기·빼기는 관리자가 합니다 — 묶을 접수는 접수 NO 로 관리자에게 요청하세요.</>}
            </p>
            {err && <div className="text-sm text-red-600">{err}</div>}
            <div className="max-h-[55vh] space-y-3 overflow-auto">
              {rows === null ? <p className="text-sm text-ink-faint">불러오는 중…</p>
                : groups.length === 0 ? <p className="text-sm text-ink-faint">묶음이 없습니다.</p>
                : groups.map((g) => (
                  <div key={g.id} className="rounded-lg border border-line">
                    <div className="flex flex-wrap items-center gap-2 bg-canvas px-3 py-2 text-sm">
                      <b>{g.name}</b>
                      <span className="text-xs text-ink-soft">접수 {g.members.length}개</span>
                      <span className="ml-auto text-xs">
                        이 달 청구 <b>{money(sum(g.members, 'cost_cust_total'))}</b> · 운행료 <b>{money(sum(g.members, 'cost_driver_total'))}</b>
                        {' '}· 수익 <b>{money(sum(g.members, 'profit'))}</b>
                      </span>
                      {canWrite && (
                        <button type="button" className="btn-ghost text-xs text-red-600" disabled={busy}
                          onClick={() => window.confirm(`「${g.name}」 묶음을 지울까요?\n묶음으로 열려 있던 접수는 자기 청구가 없으면 기사 운행료가 다시 0원이 됩니다.`)
                            && void run(() => api.saveRecvBillGroup({ op: 'drop', id: g.id }))}>묶음 지우기</button>
                      )}
                    </div>
                    <table className="w-full text-sm">
                      <thead className="text-xs text-ink-soft">
                        <tr>
                          <th className="text-right">접수 NO</th><th className="text-left">거래처</th><th>구분</th><th>시작월</th>
                          <th className="text-right">이 달 청구</th><th className="text-right">이 달 운행료</th><th className="text-right">수익</th>
                          <th>이 달</th>{canWrite && <th />}
                        </tr>
                      </thead>
                      <tbody>
                        {g.members.map((r) => (
                          <tr key={r.member_id}>
                            <td className="text-right tabular-nums">{r.recv_id}</td>
                            <td>{r.cust_name ?? '—'}</td>
                            <td className="text-center">{r.recv_type ?? ''}</td>
                            <td className="text-center tabular-nums">{r.from_mon}</td>
                            <td className="text-right tabular-nums">{money(r.cost_cust_total)}</td>
                            <td className="text-right tabular-nums">{money(r.cost_driver_total)}</td>
                            <td className="text-right tabular-nums">{money(r.profit)}</td>
                            <td className={`text-center text-xs ${STATE_TONE[r.state ?? ''] ?? ''}`}>{r.state}</td>
                            {canWrite && (
                              <td className="text-right">
                                <button type="button" className="btn-ghost text-xs" disabled={busy}
                                  onClick={() => window.confirm(`접수 ${r.recv_id} 를 「${g.name}」에서 뺄까요?`)
                                    && void run(() => api.saveRecvBillGroup({ op: 'remove', memberId: r.member_id! }))}>빼기</button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {canWrite && (
                      <div className="flex flex-wrap items-center gap-2 border-t border-line px-3 py-2 text-xs">
                        <input className="field w-28" placeholder="접수 NO" inputMode="numeric"
                          value={add[g.id]?.recvId ?? ''}
                          onChange={(e) => setAdd({ ...add, [g.id]: { fromMon: add[g.id]?.fromMon ?? mon, recvId: e.target.value.replace(/\D/g, '') } })} />
                        <label className="flex items-center gap-1">시작월
                          <input className="field w-28" type="month" value={add[g.id]?.fromMon ?? mon}
                            onChange={(e) => setAdd({ ...add, [g.id]: { recvId: add[g.id]?.recvId ?? '', fromMon: e.target.value } })} />
                        </label>
                        <button type="button" className="btn-primary text-xs" disabled={busy || !add[g.id]?.recvId}
                          onClick={() => void run(async () => {
                            await api.saveRecvBillGroup({ op: 'add', id: g.id, recvId: Number(add[g.id]!.recvId), fromMon: add[g.id]!.fromMon || mon });
                            setAdd({ ...add, [g.id]: { recvId: '', fromMon: mon } });
                          })}>접수 넣기</button>
                      </div>
                    )}
                  </div>
                ))}
            </div>
            {canWrite && (
              <div className="flex items-center gap-2 border-t border-line pt-3 text-sm">
                <input className="field w-60" placeholder="새 묶음 이름(예: 코오롱)" value={newName} onChange={(e) => setNewName(e.target.value)} />
                <button type="button" className="btn-primary" disabled={busy || !newName.trim()}
                  onClick={() => void run(async () => { await api.saveRecvBillGroup({ op: 'group', name: newName }); setNewName(''); })}>묶음 만들기</button>
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
